import React from 'react'
import { FormControl, Typography, Box } from '@mui/material'
import { type RadioButtonGroupProps, type FieldValues, AutocompleteElement } from 'react-hook-form-mui'
import CustomRadioButtonGroup, { getId } from '../../../../components/CustomRadioButtonGroup'
import { type ILinearObject } from '../../../SurveyTempleteFeature/types'
import { InvalidQuestionError } from '../../styles'
import QuestionHeader from '../../../StudentSurveyListFeature/components/QuestionHeader'
import { grey } from '../../../../colors'

interface LinearScaleCardProps extends Omit<RadioButtonGroupProps<FieldValues>, 'options'> {
  title: string
  options: ILinearObject[]
  questionNumber: number
  invalidQuestionItemId: string | undefined
  isRequired: boolean
}

const LinearScaleCard: React.FC<LinearScaleCardProps> = (
  {
    title,
    name,
    options,
    disabled = false,
    questionNumber,
    invalidQuestionItemId,
    isRequired,
    ...rest
  }
): JSX.Element => {
  const { onChange } = rest

  const preparedOptionsForDefaultSelection = React.useMemo(() => options.map(o => ({ ...o, id: o.option })), [options])
  return (
    <Box tabIndex={0} aria-label={`Survey Question ${questionNumber} ${title}`} id={name} sx={{ p: '14px 18px', border: `1px solid ${grey[200]} `, borderRadius: '10px' }}>
      <QuestionHeader onChange={onChange} isRequired={isRequired} name={name} />
      {invalidQuestionItemId && invalidQuestionItemId === name && <InvalidQuestionError>*Response required. Please answer and submit again.</InvalidQuestionError>}
      <Typography variant="h5" component="h3" color={disabled ? 'text.secondary' : 'text.primary'} my={2}>
        {questionNumber}. {title}
      </Typography>
      {
        options && options.length > 7
          ? (
            <AutocompleteElement
              name={name}
              options={preparedOptionsForDefaultSelection}
              matchId
              autocompleteProps={{
                disabled,
                size: 'medium',
                getOptionLabel: (o) => o?.option ?? '',
                onChange: (_e, opt) => onChange?.(opt ? opt.option : '')
              }}
            />
            )
          : (
            <FormControl aria-label={title} aria-owns={options?.map((value) => getId(name, value.option))?.join(' ')}>
              <CustomRadioButtonGroup
                name={name}
                type="string"
                options={options?.map((value) => ({
                  id: value.option,
                  label: value.option
                }))}
                {...rest} />
            </FormControl>
            )
      }
    </Box>
  )
}

export default LinearScaleCard
